import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, BrainCircuit, MapPin, Zap } from "lucide-react";
import { FadeUp, MaskedLine, ClipReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";

const PASTA_IMG = "/catalog/cat-primi.jpg";

const heroStats = [
  { value: "5 min", label: "Da ordine a piatto pronto" },
  { value: "0", label: "Cuochi e sala da gestire" },
  { value: "+30%", label: "Crescita annua del mercato" },
  { value: "365 gg", label: "Shelf-life materie prime" },
];

const mercato = [
  { value: "220.000+", label: "Celiaci in Italia" },
  { value: "6 mln", label: "Consumatori occasionali" },
  { value: "+30%", label: "Crescita annua del settore" },
];

const comeFunziona = [
  {
    n: "01",
    title: "Il cliente ordina dall'app",
    text: "Sfoglia il menu, compone la sua bowl e paga in 30 secondi. Zero coda in cassa.",
    img: "/risorice/app.jpg",
  },
  {
    n: "02",
    title: "MICHELCHEF® cucina",
    text: "La cottura MAGNETRON prepara il piatto in 5 minuti: perfetto, ogni volta.",
    img: "/risorice/kds.jpg",
  },
  {
    n: "03",
    title: "Ritira dal Food-Box Wall",
    text: "Sblocca il suo box termoregolato con QR o PIN. Zero attesa, zero contatti.",
    img: "/risorice/foodbox.jpg",
  },
];

const ecosistema = [
  { title: "App Mobile", text: "Il ristorante in tasca: ordina, paga e ritira.", img: "/risorice/app.jpg", tag: "iOS · Android" },
  { title: "Kiosk in-store", text: "Ordina senza scaricare l'app.", img: "/risorice/kiosk.jpg", tag: "Touch" },
  { title: "Monitor Menu", text: "Selezione piatti a schermo intero.", img: "/risorice/monitor.jpg", tag: "Digital signage" },
  { title: "Kitchen Display", text: "Stato ordini in cucina in real-time.", img: "/risorice/kds.jpg", tag: "Real-time" },
];

const michelchefPoints = [
  "Cottura MAGNETRON con sensori intelligenti — precisione ±1°C",
  "5 minuti da ordine confermato a piatto pronto",
  "Shelf-life materie prime: 365 giorni senza perdita di qualità",
  "Ciclo di pulizia automatico a fine servizio",
  "100% gluten-free · linea separata anti-contaminazione",
];

const certificazioni = [
  { title: "Spiga Barrata AIC", text: "Certificazione ufficiale Associazione Italiana Celiachia." },
  { title: "ISO 9001:2015", text: "Gestione qualità certificata su tutti i processi." },
  { title: "Buoni SSN", text: "Accettazione diretta dei buoni celiaci, redemption integrata." },
  { title: "Shelf-life 365 gg", text: "Materie prime a lunga conservazione, zero sprechi." },
];

const aiFeatures = [
  { title: "AI Ordini", text: "Chatbot che consiglia piatti, compone bowl e gestisce allergie." },
  { title: "Riordini automatici", text: "Predice le scorte e piazza gli ordini fornitori sotto-scorta." },
  { title: "AI Finanza", text: "Analizza costi, margini e food-cost, ottimizza il pricing." },
  { title: "Customer Insight", text: "Segmenta la clientela, personalizza offerte e fidelity." },
];

const locali = [
  { citta: "Roma", dettaglio: "Via A. De Gasperi · vicino al Vaticano", quando: "Q2 2026", tag: "Prossima apertura · Store pilota" },
  { citta: "Bologna", dettaglio: "Via Pradazzo · Zona Aeroporto", quando: "Q3 2026", tag: "Posti a sedere · Delivery & Take-away" },
  { citta: "Milano", dettaglio: "Via Mercato, 10", quando: "In arrivo", tag: "Solo Delivery & Take-away" },
];

const incluso = [
  "Food-Box Wall modulare da 6 a 12+ box termoregolati",
  "Cucina automatizzata MICHELCHEF® con tecnologia MAGNETRON",
  "App brandizzata, kiosk e gestionale in cloud",
  "Training dello staff e supporto marketing al lancio",
  "Progetto di layout e conto economico generati dall'AI in 60 secondi",
];

const piatti = [
  { nome: "Lasagna alla Bolognese", prezzo: "€ 4,50", img: "/risorice/piatti/p1.jpg" },
  { nome: "Risotto ai Funghi Porcini", prezzo: "€ 6,33", img: "/risorice/piatti/p2.jpg" },
  { nome: "Mezzi Rigatoni Carbonara", prezzo: "€ 5,75", img: "/risorice/piatti/p3.jpg" },
  { nome: "Spaghetti alle Vongole", prezzo: "€ 8,72", img: "/risorice/piatti/p4.jpg" },
  { nome: "Risotto alla Milanese", prezzo: "€ 4,45", img: "/risorice/piatti/p5.jpg" },
  { nome: "Linguine allo Scoglio", prezzo: "€ 7,80", img: "/risorice/piatti/p6.jpg" },
  { nome: "Cappelletti in Brodo", prezzo: "€ 5,94", img: "/risorice/piatti/p7.jpg" },
  { nome: "Tagliatelle al Ragù", prezzo: "€ 5,55", img: "/risorice/piatti/p8.jpg" },
];

const Risorice = () => (
  <div data-testid="risorice-page">
    <Seo
      title="Franchising Risorice — Il Ristorante Automatizzato Gluten Free"
      description="Apri il tuo Risorice: ristorante 100% gluten-free con MICHELCHEF® e Food-Box Wall. Chiavi in mano da € 90.000, buoni SSN accettati. Roma, Bologna, Milano."
      image="/risorice/logo-icon.png"
    />
    <section className="relative overflow-hidden bg-forest pb-0 pt-36 text-bone">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-sm uppercase tracking-[0.3em] text-ochre"
          >
            Franchising · Risorice®
          </motion.p>
          <h1
            data-testid="risorice-heading"
            className="mt-6 font-display text-5xl font-medium leading-[0.98] tracking-tight text-bone sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Il ristorante</MaskedLine>
            <MaskedLine delay={0.3}>che si serve</MaskedLine>
            <MaskedLine delay={0.45} className="italic text-moss">
              da solo.
            </MaskedLine>
          </h1>
          <FadeUp delay={0.65} className="mt-8 max-w-lg">
            <p className="leading-relaxed text-bone/80">
              Cucina italiana 100% gluten-free preparata da MICHELCHEF® con tecnologia MAGNETRON
              in 5 minuti, ritirata dal Food-Box Wall. Zero coda, zero contatti, zero personale
              di sala: solo delivery e take-away.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/affiliazione"
                data-testid="risorice-hero-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-ochre hover:text-forest hover:shadow-xl hover:shadow-moss/30"
              >
                Candidati ora
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="#come-funziona"
                data-testid="risorice-hero-secondary"
                className="inline-flex items-center gap-3 rounded-full border border-bone/25 px-8 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-bone/10"
              >
                Vedi come funziona
              </a>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <ClipReveal delay={0.5} className="relative">
            <img
              data-testid="risorice-hero-image"
              src="/risorice/logo-icon.png"
              alt="Logo Risorice"
              className="aspect-square w-full rounded-2xl object-cover"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -left-6 bottom-10 rounded-full bg-moss px-6 py-3 text-sm uppercase tracking-[0.15em] text-bone shadow-2xl"
            >
              Zero coda · Zero contatti
            </motion.div>
          </ClipReveal>
        </div>
      </div>
      <div className="mt-20 border-t border-bone/10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-bone/10">
          {heroStats.map((s, i) => (
            <FadeUp key={s.label} delay={0.2 + i * 0.08} className="px-6 py-10 text-center">
              <p className="font-display text-4xl font-extrabold text-ochre sm:text-5xl">{s.value}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] text-bone/60">{s.label}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <Marquee
      items={["100% Gluten Free", "Magnetron · 5 min", "Zero coda", "Buoni SSN", "Food-Box Wall", "Michelchef®"]}
      className="border-b border-forest/10"
    />

    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <FadeUp>
        <p className="text-sm uppercase tracking-[0.3em] text-moss">Il mercato</p>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
          Un mercato che cresce del 30% l'anno. <span className="italic text-moss">Tu guardi o vendi?</span>
        </h2>
        <p className="mt-8 max-w-2xl leading-relaxed text-forest/70">
          Oltre 220.000 celiaci in Italia e 6 milioni di consumatori occasionali cercano ogni giorno
          un'offerta gluten-free seria. Risorice è l'unico format automatizzato 100% gluten-free
          con buoni SSN accettati: la domanda c'è già, la concorrenza no.
        </p>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {mercato.map((m, i) => (
          <FadeUp key={m.label} delay={i * 0.1}>
            <div data-testid={`mercato-${i}`} className="border-l-2 border-moss bg-white p-8">
              <p className="font-display text-5xl font-extrabold text-forest">{m.value}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] text-forest/60">{m.label}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    <section id="come-funziona" className="scroll-mt-24 bg-sage/15 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-moss">Come funziona</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
            3 passi. 5 minuti. <span className="italic text-ochre">Zero attese.</span>
          </h2>
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {comeFunziona.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.12}>
              <div
                data-testid={`step-${s.n}`}
                className="group h-full border border-forest/10 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-moss/10"
              >
                <div className="overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <span className="font-display text-4xl font-extrabold text-outline">{s.n}</span>
                  <h3 className="mt-4 font-display text-2xl font-bold text-forest">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest/70">{s.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-forest py-28 text-bone lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-sage">App, Kiosk, Monitor & Kitchen Display</p>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-none sm:text-5xl">
            Dallo smartphone del cliente <span className="italic text-ochre">allo schermo della cucina.</span>
          </h2>
          <p className="mt-6 max-w-xl text-bone/70">
            L'ecosistema Risorice connette ogni touchpoint dello store: niente da inventare,
            è tutto già pronto.
          </p>
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ecosistema.map((e, i) => (
            <FadeUp key={e.title} delay={i * 0.1}>
              <div
                data-testid={`ecosistema-${e.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group h-full border border-bone/10 bg-forest transition-[background-color,transform] duration-500 hover:-translate-y-2"
              >
                <div className="overflow-hidden">
                  <img
                    src={e.img}
                    alt={e.title}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-ochre">{e.tag}</p>
                  <h3 className="mt-2 font-display text-xl font-bold">{e.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-bone/65">{e.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-moss">Michelchef® · Food-Box Wall</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
            Ogni piatto perfetto, <span className="italic text-moss">ogni volta.</span>
          </h2>
          <p className="mt-8 leading-relaxed text-forest/70">
            MICHELCHEF® è il sistema di cottura MAGNETRON automatizzato che bilancia potenza, tempo
            e umidità per ogni singolo piatto. Il Food-Box Wall è il cuore visivo dello store:
            un muro di box modulari termoregolati che il cliente sblocca con QR o PIN.
          </p>
          <ul className="mt-10 space-y-4">
            {michelchefPoints.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-forest/80">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-moss" />
                {p}
              </li>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="overflow-hidden rounded-2xl">
            <img
              data-testid="foodbox-image"
              src="/risorice/foodbox.jpg"
              alt="Food-Box Wall Risorice"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </FadeUp>
      </div>
    </section>

    <section className="border-y border-forest/10 bg-white py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-moss">Certificazioni & garanzie</p>
          <h2 className="mt-6 font-display text-3xl font-medium leading-tight text-forest sm:text-4xl">
            Un format serio, tracciato e conforme.
          </h2>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {certificazioni.map((c, i) => (
            <FadeUp key={c.title} delay={i * 0.08}>
              <div data-testid={`cert-${i}`} className="h-full border border-forest/10 p-7">
                <ShieldCheck size={26} className="text-moss" />
                <h3 className="mt-5 font-display text-lg font-bold text-forest">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-forest/65">{c.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-forest py-28 text-bone lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-sage">Intelligenza Artificiale</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none sm:text-5xl">
            L'AI che gestisce tutto. <span className="italic text-ochre">Tu incassi.</span>
          </h2>
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {aiFeatures.map((a, i) => (
            <FadeUp key={a.title} delay={i * 0.08}>
              <div data-testid={`ai-${i}`} className="h-full border border-bone/10 p-7 transition-colors duration-500 hover:bg-bone/5">
                <BrainCircuit size={26} className="text-ochre" />
                <h3 className="mt-5 font-display text-lg font-bold">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-bone/65">{a.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.15}>
          <div className="mt-24 border-t border-bone/10 pt-16">
            <p className="text-sm uppercase tracking-[0.3em] text-sage">I nostri locali</p>
            <h3 className="mt-6 font-display text-3xl font-medium leading-tight sm:text-4xl">
              In espansione in tutta Italia.
            </h3>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {locali.map((l) => (
                <div
                  key={l.citta}
                  data-testid={`locale-${l.citta.toLowerCase()}`}
                  className="border border-bone/10 p-8 transition-colors duration-500 hover:bg-bone/5"
                >
                  <div className="flex items-center justify-between">
                    <MapPin size={20} className="text-ochre" />
                    <span className="rounded-full bg-moss/20 px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] text-ochre">
                      {l.quando}
                    </span>
                  </div>
                  <p className="mt-6 font-display text-3xl font-bold uppercase tracking-wide">{l.citta}</p>
                  <p className="mt-2 text-sm text-bone/65">{l.dettaglio}</p>
                  <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-sage">{l.tag}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    <section className="bg-sage/15 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-moss">Il menu</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
            Piatti che i clienti <span className="italic text-moss">riordinano.</span>
          </h2>
          <p className="mt-6 max-w-xl leading-relaxed text-forest/70">
            Menu 100% gluten-free certificato Spiga Barrata AIC, packaging biodegradabile,
            buoni SSN celiaci accettati. Prezzi al pubblico pensati per volumi alti.
          </p>
        </FadeUp>
        <div className="mt-14 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {piatti.map((p, i) => (
            <FadeUp key={p.nome} delay={(i % 4) * 0.08}>
              <div
                data-testid={`piatto-${i}`}
                className="group h-full border border-forest/10 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-moss/10"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.nome}
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 p-4">
                  <p className="text-sm font-medium text-forest">{p.nome}</p>
                  <p className="shrink-0 font-display text-base font-bold text-moss">{p.prezzo}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <FadeUp>
          <p className="flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-moss">
            <Zap size={16} />
            L'offerta franchising
          </p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-forest sm:text-5xl">
            Chiavi in mano da € 90k. <span className="italic text-moss">Ti richiamiamo entro 48 ore.</span>
          </h2>
          <ul className="mt-10 space-y-4">
            {incluso.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-forest/80">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-moss" />
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/affiliazione"
              data-testid="risorice-contact-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-moss px-10 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30"
            >
              Candidati ora
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/simulatore"
              data-testid="risorice-simulatore-cta"
              className="inline-flex items-center gap-3 rounded-full border border-forest/20 px-10 py-4 text-sm uppercase tracking-[0.15em] text-forest transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-moss hover:bg-moss/10"
            >
              Apri il simulatore
            </Link>
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="overflow-hidden rounded-2xl">
            <img
              data-testid="risorice-cta-image"
              src={PASTA_IMG}
              alt="Piatto gluten free del menu Risorice"
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default Risorice;
